import clsx from 'clsx';
import { Spinner } from 'components/UI';
import { TableBody } from 'components/UI/V3/Table/Body/TableBody';
import { TableCell } from 'components/UI/V3/Table/Cell/TableCell';
import { TableHeader } from 'components/UI/V3/Table/Header/TableHeader';
import { TableHeaderCell } from 'components/UI/V3/Table/HeaderCell/TableHeaderCell';
import { TableRow } from 'components/UI/V3/Table/Row/TableRow';
import { Table } from 'components/UI/V3/Table/Table';
import React from 'react';
import { DEMO_LOCATIONS, getOverviewData, SOURCES } from '../data/mockData';
import styles from './LocationComparisonTable.module.scss';

interface LocationComparisonTableProps {
    locationIds: Array<number>;
    loading: boolean;
}

/*
 * Rows are locations, columns are sources, cells are each source's share of
 * that location's new clients. The row max is highlighted so mix differences
 * between locations read at a glance.
 */
export const LocationComparisonTable: React.FC<LocationComparisonTableProps> = props => {
    const visibleLocations =
        props.locationIds.length === 0
            ? DEMO_LOCATIONS
            : DEMO_LOCATIONS.filter(location => props.locationIds.includes(location.id));

    const sources = SOURCES.filter(source => source.id !== 'unattributed');

    const rows = visibleLocations.map(location => {
        const data = getOverviewData([location.id]);
        const attributedTotal = data.rows
            .filter(row => row.source.id !== 'unattributed')
            .reduce((sum, row) => sum + row.newClients, 0);

        const shares = sources.map(source => {
            const row = data.rows.find(dataRow => dataRow.source.id === source.id);
            return attributedTotal === 0 ? 0 : (row?.newClients ?? 0) / attributedTotal;
        });

        return {
            location,
            newClients: data.totalNewClients,
            shares,
            maxShare: Math.max(...shares)
        };
    });

    return (
        <Table firstColumnFixed>
            <TableHeader>
                <TableHeaderCell>Location</TableHeaderCell>
                <TableHeaderCell>New Clients</TableHeaderCell>
                {sources.map(source => (
                    <TableHeaderCell key={source.id}>{source.name}</TableHeaderCell>
                ))}
            </TableHeader>
            <TableBody>
                {props.loading && (
                    <tr>
                        <td
                            colSpan={sources.length + 2}
                            className={styles.loadingCell}
                        >
                            <Spinner />
                        </td>
                    </tr>
                )}
                {!props.loading &&
                    rows.map(row => (
                        <TableRow key={row.location.id}>
                            <TableCell>{row.location.name}</TableCell>
                            <TableCell className={styles.totalCell}>{row.newClients}</TableCell>
                            {row.shares.map((share, index) => (
                                <TableCell
                                    key={sources[index].id}
                                    className={clsx(share === row.maxShare && share > 0 && styles.topShareCell)}
                                >
                                    {`${Math.round(share * 100)}%`}
                                </TableCell>
                            ))}
                        </TableRow>
                    ))}
            </TableBody>
        </Table>
    );
};

export default LocationComparisonTable;
